import { MAX_PLAYERS } from "@coup/shared";
import { useState } from "react";
import { ICONS } from "../art";
import { Avatar } from "../components/Avatar";
import { DevPanel } from "../components/DevPanel";
import { Icon } from "../components/Icon";
import { Spectators } from "../components/Spectators";
import { ProfileModal } from "../components/ProfileModal";
import { Stage, fromYou, seatPositions } from "../components/Stage";
import stage from "../components/Stage.module.css";
import { Stepper } from "../components/Stepper";
import ui from "../components/ui.module.css";
import { useStore } from "../state/store";
import styles from "./Lobby.module.css";

export function Lobby() {
  const lobby = useStore((s) => s.lobby)!;
  const you = useStore((s) => s.you);
  const send = useStore((s) => s.send);
  const leave = useStore((s) => s.leave);
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  const seats = lobby.seats.filter((s) => !s.left);
  const me = [...seats, ...lobby.spectators].find((s) => s.id === you);
  const spectating = lobby.spectators.some((s) => s.id === you);
  const isHost = lobby.hostId === you;
  const around = fromYou(seats, you);
  const positions = seatPositions(6, 330, 320);

  const copy = () => {
    void navigator.clipboard.writeText(lobby.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={styles.layout}>
      <aside className={styles.left}>
        <div className={styles.codeLabel}>Lobby code</div>
        <div className={styles.code}>{lobby.code}</div>
        <button
          type="button"
          className={ui.ghost}
          style={{ alignSelf: "flex-start", padding: "0 14px" }}
          onClick={copy}
        >
          <Icon path={ICONS.copy} size={16} />
          {copied ? "Copied" : "Copy code"}
        </button>
        <div className={styles.note}>Share the code above to invite friends.</div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className={ui.ghost}
          style={{ color: "var(--muted)" }}
          onClick={leave}
        >
          Leave lobby
        </button>
      </aside>

      <main className={styles.main}>
        <Stage>
          <div className={stage.felt} />
          <div className={styles.centre}>
            <div className={styles.count}>
              {seats.length} of {MAX_PLAYERS} seated
            </div>
            {spectating && (
              <button
                type="button"
                className={`${ui.primary} ${styles.sit}`}
                disabled={seats.length >= MAX_PLAYERS}
                onClick={() => send({ t: "sit" })}
              >
                {seats.length >= MAX_PLAYERS ? "Table full" : "Take a seat"}
              </button>
            )}
            <div className={styles.waiting}>
              {lobby.phase === "over"
                ? "Match over · waiting for the host"
                : "Waiting for the host to start"}
            </div>
          </div>
          {positions.map((pos, i) => {
            const s = around[i];
            return (
              <div
                key={s?.id ?? `open${i}`}
                className={styles.seat}
                style={{ left: pos.x, top: pos.y }}
              >
                {s ? (
                  <>
                    <Avatar
                      avatar={s.avatar}
                      size={64}
                      ring={3}
                      style={{
                        boxShadow: "0 0 0 2px var(--walnut)",
                        opacity: s.connected ? 1 : 0.45,
                      }}
                    />
                    <div className={styles.plate}>
                      {s.name}
                      {s.id === lobby.hostId && <span className={styles.hostBadge}>HOST</span>}
                    </div>
                    {!s.connected && <div className={styles.offline}>Disconnected</div>}
                  </>
                ) : (
                  <>
                    <div className={styles.openSeat} />
                    <div className={styles.openLabel}>Open seat</div>
                  </>
                )}
              </div>
            );
          })}
        </Stage>
        <Spectators />
      </main>

      <aside className={styles.right}>
        <div className={styles.heading}>{isHost ? "Host controls" : "The table"}</div>
        <div className={styles.setting}>
          <div style={{ fontSize: 16 }}>Response window</div>
          {isHost ? (
            <Stepper
              seconds={lobby.responseMs / 1000}
              onChange={(s) => send({ t: "host", op: { op: "responseMs", ms: s * 1000 } })}
            />
          ) : (
            <div className={ui.stepValue}>{lobby.responseMs / 1000} s</div>
          )}
        </div>
        <div className={styles.rule} />
        <div className={styles.playersHeader}>
          <span>Players</span>
          {isHost && seats.length > 1 && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => send({ t: "host", op: { op: "shuffle" } })}
            >
              <Icon path={ICONS.shuffle} size={13} />
              Shuffle seats
            </button>
          )}
        </div>
        <div className={styles.players}>
          {seats.map((s, i) => (
            <div key={s.id} className={styles.player}>
              <Avatar avatar={s.avatar} size={30} ring={2} />
              <div style={{ flex: 1, fontSize: 16 }}>{s.name}</div>
              {isHost && (
                <div className={styles.move}>
                  <button
                    type="button"
                    aria-label={`Move ${s.name} up`}
                    disabled={i === 0}
                    onClick={() =>
                      send({ t: "host", op: { op: "move", playerId: s.id, index: i - 1 } })
                    }
                  >
                    <Icon path={ICONS.up} size={14} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${s.name} down`}
                    disabled={i === seats.length - 1}
                    onClick={() =>
                      send({ t: "host", op: { op: "move", playerId: s.id, index: i + 1 } })
                    }
                  >
                    <Icon path={ICONS.down} size={14} />
                  </button>
                </div>
              )}
              {s.id === you ? (
                <span className={styles.youTag}>{isHost ? "You · host" : "You"}</span>
              ) : isHost ? (
                <button
                  type="button"
                  className={styles.kick}
                  aria-label={`Kick ${s.name}`}
                  onClick={() => send({ t: "host", op: { op: "kick", playerId: s.id } })}
                >
                  Kick
                </button>
              ) : (
                s.id === lobby.hostId && <span className={styles.youTag}>Host</span>
              )}
            </div>
          ))}
        </div>
        <button type="button" className={ui.ghost} onClick={() => setEditing(true)}>
          Edit name and avatar
        </button>
        <div style={{ flex: 1 }} />
        {lobby.devTools && isHost && <DevPanel />}
        <div className={styles.note} style={{ fontSize: 13 }}>
          {spectating
            ? "You're spectating. Take a free seat to play in the next match."
            : "New players can join until each match commences. Later arrivals spectate."}
        </div>
        {isHost ? (
          <button
            type="button"
            className={ui.primary}
            style={{ height: 56, fontSize: 24 }}
            disabled={seats.length < 2}
            onClick={() => send({ t: "host", op: { op: "start" } })}
          >
            Start match · {seats.length} {seats.length === 1 ? "player" : "players"}
          </button>
        ) : (
          <div className={styles.waitingHost}>Waiting for the host to start</div>
        )}
      </aside>

      {editing && me && (
        <ProfileModal name={me.name} avatar={me.avatar} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}
